import { type FC, useEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

interface CheckboxProps {
  isChecked: boolean;
  onChange: (value: boolean) => void;
  className?: string;
  label?: string;
  isDisabled?: boolean;
  /** Renders the mixed (dash) state — neither fully checked nor unchecked.
   * Useful for "select all" headers when only some rows are selected. */
  isIndeterminate?: boolean;
  /** Accessible name for checkboxes rendered without a visible `label`
   * (e.g. row-selection checkboxes in a table). */
  accessibleLabel?: string;
}

export const Checkbox: FC<CheckboxProps> = ({
  isChecked,
  onChange,
  className,
  label,
  isDisabled,
  isIndeterminate = false,
  accessibleLabel,
}) => {
  // Generate a unique ID for each checkbox to support multiple instances
  const id = useMemo(
    () => `checkbox-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  // `indeterminate` is a DOM property only — it can't be set via an
  // attribute, so mirror the prop onto the input through a ref.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  return (
    <label
      className={twMerge("flex cursor-pointer items-center", className)}
      htmlFor={id}
    >
      <input
        ref={inputRef}
        aria-label={accessibleLabel}
        checked={isChecked}
        className="h-4 w-4 rounded-sm border-border-strong text-indigo-600 focus:border-indigo-500 focus:ring-indigo-500"
        id={id}
        onChange={({ target: { checked } }) => {
          onChange(checked);
        }}
        type="checkbox"
        disabled={isDisabled}
      />
      {label ? (
        <span className="ml-2 text-sm text-foreground">{label}</span>
      ) : null}
    </label>
  );
};
